import React, { useEffect, useState } from 'react';
import { Box, Modal, Typography, TextField, Button, Checkbox, CircularProgress } from '@mui/material';
import { FileText } from 'lucide-react';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { useIconScale } from '../state/useIconScale';
import { formatMoneda } from '../state/format';
import { centroApi } from '../api/centroApi';
import { CentroApiError, VersionCentro } from '../types';
import { BolsaCentro } from '../types';
import { formatFechaCorta, hoyMas15Bogota } from '../state/formatVersiones';
import { clienteService } from '../../../../clientes/services/clienteService';
import { ContactoCliente } from '../../../../clientes/types/Cliente';
import { useToast } from './Toast';

interface SendQuoteModalProps {
  bolsa: BolsaCentro;
  siguienteVersion: number;
  // SPEC-206 (RN-009) — la versión que el cliente tiene hoy (la que esta reemplaza).
  versionVigente?: VersionCentro | null;
  onCerrar: () => void;
  onEnviada: () => void;
}

// SPEC-167 (RN-007/CA-008) — el modal hereda la validez construida de la bolsa; si es null,
// sugiere hoy+15 (reemplaza el default anterior "mes siguiente").
function hoyMas15ISO(): string {
  const fecha = new Date();
  fecha.setDate(fecha.getDate() + 15);
  return fecha.toISOString().slice(0, 10);
}

// RN-004 — solo contactos activos con correo son seleccionables (mismo filtro que revalida el backend)
function contactosElegibles(contactos: ContactoCliente[]): ContactoCliente[] {
  return contactos.filter((c) => c.activo && !!c.correoElectronico?.trim());
}

export const SendQuoteModal: React.FC<SendQuoteModalProps> = ({ bolsa, siguienteVersion, versionVigente, onCerrar, onEnviada }) => {
  const { showToast } = useToast();
  const [contactos, setContactos] = useState<ContactoCliente[]>([]);
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set());
  const esReenvio = siguienteVersion > 1;
  // SPEC-206 (RN-008/CA-012) — al reenviar, la validez se reinicia a hoy (Bogotá) + 15 aunque la
  // cotización tenga otra guardada; el primer envío conserva la de construcción (SPEC-167).
  const [validez, setValidez] = useState(esReenvio ? hoyMas15Bogota() : bolsa.validez ?? hoyMas15ISO());
  const [enviando, setEnviando] = useState(false);
  const fileTextSize = useIconScale(16);

  useEffect(() => {
    if (!bolsa.cliente_id) return;
    clienteService
      .obtener(bolsa.cliente_id)
      .then((cliente) => {
        setContactos(contactosElegibles(cliente.contactos));
      })
      .catch(() => {
        // Sin bloquear la apertura del modal — el checklist queda vacío (PipelineTab ya
        // bloqueó antes de abrir si no había contactos elegibles; esto solo cubre el caso
        // de que la carga del detalle del cliente falle en este punto puntual)
      });
  }, [bolsa.cliente_id]);

  // SPEC-206 (RN-009) — desde la v2 el asunto lleva " · vN" después del consecutivo.
  const asunto = `Cotización ${bolsa.codigo_cotizacion ?? bolsa.id}${esReenvio ? ` · v${siguienteVersion}` : ''} — CVprinting (válida hasta ${validez})`;
  const nombrePdf = `${(bolsa.codigo_cotizacion ?? bolsa.id).toLowerCase()}-v${siguienteVersion}.pdf`;

  const alternarContacto = (contactoId: string) => {
    setSeleccionados((prev) => {
      const siguiente = new Set(prev);
      if (siguiente.has(contactoId)) {
        siguiente.delete(contactoId);
      } else {
        siguiente.add(contactoId);
      }
      return siguiente;
    });
  };

  const handleEnviar = async () => {
    const destinatarios = contactos
      .filter((c) => c.id && seleccionados.has(c.id))
      .map((c) => ({
        contacto_id: c.id as string,
        correo: c.correoElectronico,
        nombre: `${c.nombres} ${c.apellidos}`.trim(),
      }));

    if (destinatarios.length === 0) {
      showToast('Selecciona al menos un contacto antes de enviar', 'error');
      return;
    }

    setEnviando(true);
    try {
      await centroApi.enviarCotizacion(bolsa.id, { destinatarios, subject: asunto, validez });
      showToast(
        `${bolsa.codigo_cotizacion ?? bolsa.id} enviada a ${destinatarios.length} contacto${destinatarios.length > 1 ? 's' : ''} — PDF v${siguienteVersion}`,
      );
      onEnviada();
      onCerrar();
    } catch (error) {
      if (error instanceof CentroApiError) {
        const fallidos = error.detalle?.filter((d) => d.estado === 'fallido') ?? [];
        const mensaje =
          fallidos.length > 0
            ? `${error.message}: ${fallidos.map((d) => d.correo).join(', ')}`
            : error.message;
        showToast(mensaje, 'error');
      } else {
        showToast('Error al enviar la cotización', 'error');
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal open onClose={onCerrar} data-testid="send-quote-modal">
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 440,
          maxWidth: '92vw',
          backgroundColor: '#fff',
          borderRadius: '20px',
          padding: '30px 32px',
          boxShadow: '0 24px 60px rgba(46,36,48,.28)',
        }}
      >
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), fontWeight: 600, letterSpacing: '2.2px', textTransform: 'uppercase', color: TOKENS.muted, marginBottom: '6px' }}>
          {esReenvio ? 'REENVÍO · NUEVA VERSIÓN' : 'ENVIAR COTIZACIÓN'}
        </Typography>
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(19), fontWeight: 700, color: TOKENS.ink, marginBottom: '6px' }}>
          {bolsa.codigo_cotizacion ?? bolsa.id} → {bolsa.cliente_razon_social ?? 'cliente'}{esReenvio ? ` · v${siguienteVersion}` : ''}
        </Typography>
        {esReenvio && (
          <Typography data-testid="send-modal-version" sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), fontWeight: 600, color: TOKENS.ink }}>
            Se enviará la v{siguienteVersion}
            {versionVigente?.enviada_en ? ` · reemplaza a la v${versionVigente.version} del ${formatFechaCorta(versionVigente.enviada_en)}` : ''}
          </Typography>
        )}
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), color: TOKENS.muted, marginBottom: '18px' }}>
          El PDF de la v{siguienteVersion} se genera al enviar con la validez elegida y queda guardado en el historial.
        </Typography>

        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), fontWeight: 600, textTransform: 'uppercase', color: TOKENS.muted, marginBottom: '4px' }}>
          PARA
        </Typography>
        <Box
          data-testid="send-modal-contactos"
          sx={{
            border: `1px solid ${TOKENS.hair}`,
            borderRadius: '12px',
            marginBottom: '14px',
            maxHeight: 180,
            overflowY: 'auto',
          }}
        >
          {contactos.map((contacto) => (
            <Box
              key={contacto.id}
              onClick={() => contacto.id && alternarContacto(contacto.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 8px',
                cursor: 'pointer',
                '&:not(:last-of-type)': { borderBottom: `1px solid ${TOKENS.hair}` },
              }}
            >
              <Checkbox
                data-testid={`contacto-checkbox-${contacto.id}`}
                checked={!!contacto.id && seleccionados.has(contacto.id)}
                onChange={() => contacto.id && alternarContacto(contacto.id)}
                onClick={(e) => e.stopPropagation()}
                sx={{ color: TOKENS.purpleSoft, '&.Mui-checked': { color: TOKENS.purple } }}
              />
              <Box sx={{ paddingY: '6px' }}>
                <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13.5), fontWeight: 500, color: TOKENS.ink }}>
                  {contacto.nombres} {contacto.apellidos}
                </Typography>
                <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.muted }}>
                  {contacto.correoElectronico}
                </Typography>
              </Box>
            </Box>
          ))}
          {contactos.length === 0 && (
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), color: TOKENS.muted, padding: '10px 12px' }}>
              Cargando contactos…
            </Typography>
          )}
        </Box>

        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), fontWeight: 600, textTransform: 'uppercase', color: TOKENS.muted, marginBottom: '4px' }}>
          ASUNTO
        </Typography>
        <TextField fullWidth size="small" value={asunto} InputProps={{ readOnly: true }} sx={{ marginBottom: '14px' }} />

        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), fontWeight: 600, textTransform: 'uppercase', color: TOKENS.muted, marginBottom: '4px' }}>
          VÁLIDA HASTA
        </Typography>
        <TextField
          data-testid="send-modal-validez"
          fullWidth
          size="small"
          type="date"
          value={validez}
          onChange={(e) => setValidez(e.target.value)}
          helperText={esReenvio ? 'Reiniciada para esta versión' : undefined}
          sx={{ marginBottom: '18px' }}
        />

        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', backgroundColor: TOKENS.wash, borderRadius: '12px', padding: '10px 12px', marginBottom: '16px' }}>
          <Box sx={{ width: 38, height: 38, borderRadius: '11px', backgroundColor: 'rgba(147,64,143,.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FileText size={fileTextSize} color={TOKENS.purple} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), fontWeight: 600, color: TOKENS.ink }}>{nombrePdf}</Typography>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.muted }}>
              Se genera al enviar · {bolsa.lineas_count} referencias · {formatMoneda(bolsa.total)}
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11.5), color: TOKENS.muted, marginBottom: '20px' }}>
          La política comercial se valida aquí: si la utilidad o el descuento están fuera de rango, el envío se
          bloquea (la aprobación es una regla, no un estado). Si el correo falla para alguno de los seleccionados,
          ninguno cuenta como enviado y la cotización no cambia de estado — se reintenta sin duplicar.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <Button onClick={onCerrar} sx={{ textTransform: 'none', fontFamily: 'Poppins, sans-serif', border: `1px solid ${TOKENS.hair}`, color: TOKENS.text }}>
            Cancelar
          </Button>
          <Button
            data-testid="send-modal-confirm"
            disabled={enviando || seleccionados.size === 0}
            onClick={handleEnviar}
            startIcon={enviando ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{
              textTransform: 'none',
              fontFamily: 'Poppins, sans-serif',
              backgroundColor: TOKENS.purple,
              color: '#fff',
              borderRadius: '999px',
              padding: '8px 20px',
              '&:hover': { backgroundColor: TOKENS.purpleDark },
              '&.Mui-disabled': { backgroundColor: TOKENS.faint, color: '#fff' },
            }}
          >
            Generar PDF y enviar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
